////////////////////////////////////////////
// TOP NAV — THE ONE SITE HEADER (EVERY PAGE, INCLUDING HOME + ADMIN)
////////////////////////////////////////////
//
// FULL-WIDTH ON EVERY PAGE. DESKTOP (lg+): LOGO + LABELED LINK ROW + WALLET
// CHIP. MOBILE: SLIM TOP BAR (LOGO + WALLET CHIP) — NAVIGATION LIVES IN THE
// ICON-ONLY BottomNav TAB BAR; THERE IS NO HAMBURGER. THE LINK TABLE
// (nav/links.ts) DECIDES WHICH ITEMS APPEAR WHERE.
//
// TWO VARIANTS:
//   solid   — STICKY, BLURRED, BORDERED (EVERY STANDARD PAGE)
//   overlay — TRANSPARENT, ABSOLUTE OVER THE HOME HERO VIDEO
//
// EVERY LINK CARRIES net.navQuery SO NAVIGATION NEVER SILENTLY DROPS THE
// ACTIVE NET. ADMIN ITEMS RENDER ONLY FOR THE admin ROLE. z-40 KEEPS THE
// WALLET SHEET (RENDERED INSIDE THIS NAV) ABOVE THE z-30 BOTTOM BAR.
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { useNetwork } from "@/config/useNetwork";
import { useRole } from "@/app/providers/AuthProvider";
import { ConnectButton } from "@/app/layout/ConnectButton";
import { BottomNav } from "@/app/layout/nav/BottomNav";
import { isActiveLink, NAV_LINKS, type NavLinkDef } from "@/app/layout/nav/links";

export type NavVariant = "solid" | "overlay";

// DESKTOP LINK — ICON + LABEL, HIDDEN BELOW lg (BottomNav COVERS MOBILE)
function DesktopLink({ link, suffix }: { link: NavLinkDef; suffix: string }) {

  const { pathname } = useLocation();
  const [search] = useSearchParams();
  const active = isActiveLink(link, pathname, search);
  return (
    <Link
      to={link.to + suffix}
      aria-current={active ? "page" : undefined}
      className={
        "hidden lg:inline-flex items-center gap-[7px] text-[13px] font-semibold tracking-[.5px] uppercase whitespace-nowrap " +
        (active ? "text-blaze" : "text-fg/90 hover:text-blaze")
      }
    >
      <link.Icon />{link.label}
    </Link>
  );
}

export function TopNav({ variant = "solid", showConnect = true }: { variant?: NavVariant; showConnect?: boolean }) {

  const net = useNetwork();
  const { isAdmin } = useRole();
  const links = NAV_LINKS.filter((l) => !l.adminOnly || isAdmin);

  return (
    <>
      <nav
        aria-label="Main"
        className={
          variant === "overlay"
            ? "absolute top-0 inset-x-0 z-40"
            : "sticky top-0 z-40 border-b border-line"
        }
      >
        {/* BLUR ON A CHILD LAYER, NEVER ON THE NAV ITSELF — A backdrop-filter
            ELEMENT BECOMES THE CONTAINING BLOCK FOR position:fixed DESCENDANTS,
            WHICH WOULD PIN THE WALLET BOTTOM SHEET TO THE NAV INSTEAD OF THE
            VIEWPORT */}
        {variant === "solid" && (
          <div className="absolute inset-0 -z-10 bg-bg/86 backdrop-blur-[8px]" aria-hidden="true" />
        )}
        <div className="px-4 lg:px-10 py-[10px] lg:py-[14px] flex items-center gap-4 lg:gap-5">
          {/* shrink-0 — THE LOGO IS NEVER THE ITEM THAT GETS CRUSHED IF THE
              ROW EVER TIGHTENS (LONGER WALLET ADDRESS, EXTRA LINK) */}
          <Link to={"/" + net.navQuery} title="home" className="shrink-0">
            <img className="h-[34px] lg:h-[42px] w-auto block" src="/logo.png" alt="fuze" />
          </Link>

          <div className="ml-auto flex items-center gap-4 lg:gap-[22px] min-w-0">
            {links.filter((l) => l.desktop).map((l) => <DesktopLink key={l.to} link={l} suffix={net.navQuery} />)}
            {showConnect && <ConnectButton />}
          </div>
        </div>
      </nav>

      {/* MOBILE TAB BAR — THE mobile-FLAGGED SUBSET OF THE SAME LINK TABLE */}
      <BottomNav links={links.filter((l) => l.mobile)} suffix={net.navQuery} />
    </>
  );
}
